declare const gon: any;

const VISIT_ID_KEY: string = "aob_visit_id";

/**
 * Generates an RFC4122 v4-ish UUID without any external dependency.
 */
function generateUuid(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c: string): string => {
        const r: number = Math.random() * 16 | 0;
        const v: number = c === "x" ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

/**
 * Returns the current visit's id, generating and persisting one in
 * sessionStorage on first use. One id per browser tab/visit, so funnel
 * events fired before a Search exists (clicked_price, landing_page_loaded)
 * can still be tied together with later ones.
 */
export function getVisitId(): string {
    try {
        let visitId: string = sessionStorage.getItem(VISIT_ID_KEY);

        if (!visitId) {
            visitId = generateUuid();
            sessionStorage.setItem(VISIT_ID_KEY, visitId);
        }

        return visitId;
    } catch (_e) {
        // sessionStorage can throw (Safari private mode, some webviews) -
        // fall back so this never blocks the caller (e.g. an error popup).
        return generateUuid();
    }
}

/**
 * Called once per page load (packs/vue_helper.js) so the visit_id exists
 * before any funnel event needs to reference it.
 */
export function initFunnelTracking(): void {
    getVisitId();
}

let landingRenderedAt: number = null;

function landingKey(searchId: number | string): string {
    return `aob_funnel_landing_${searchId}`;
}

/**
 * Marks the moment the landing page (step1) finished rendering, so later
 * events can report duration since landing. Persisted in sessionStorage
 * per search_id so a mobile location.reload() (step_loading.js, once a
 * pending search becomes ready) doesn't reset it.
 *
 * @returns false if this search's landing was already marked (e.g. this
 *   is that reload) - callers use this to avoid re-sending events that
 *   already fired once.
 */
export function markLandingRendered(searchId: number | string): boolean {
    try {
        const key = landingKey(searchId);
        const existing = sessionStorage.getItem(key);

        if (existing) {
            landingRenderedAt = Number(existing);
            return false;
        }

        landingRenderedAt = Date.now();
        sessionStorage.setItem(key, landingRenderedAt.toString());
        return true;
    } catch (_e) {
        // sessionStorage can throw (see getVisitId) - degrade to
        // in-memory-only rather than breaking the whole page.
        landingRenderedAt = Date.now();
        return true;
    }
}

export function getLandingRenderedAt(): number {
    return landingRenderedAt;
}

function detectDevice(): string {
    return /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop";
}

interface FunnelEventData {
    search_id?: number | string;
    clicked_price?: number;
    duration_ms?: number;
    error_type?: string;
    message?: string;
}

/**
 * Logs a funnel-tracking event to Elasticsearch (via /funnel_events -> F-ENGINE
 * -> the "funnel_events" index), correlated by visit_id.
 *
 * No-ops entirely for non-meta visits (gon.from_meta falsy) - this funnel is
 * specifically about meta clickout performance, so visits that never came
 * from a meta clickout aren't sent at all, not just tagged. Reads gon.from_meta
 * directly (not a module-level flag set by another entry point) because each
 * webpack pack is a separate bundle with its own copy of this module - a flag
 * set from vue_helper.js's copy would never be visible here. gon itself is a
 * real global (window.gon), so every bundle sees the same, correct value.
 */
export function logFunnelEvent(event: string, data: FunnelEventData = {}): void {
    if (!gon.from_meta) {
        return;
    }

    $.post("/funnel_events", {
        visit_id: getVisitId(),
        device: detectDevice(),
        event: event,
        from_meta: gon.from_meta,
        url: document.location.href,
        search_id: data.search_id,
        clicked_price: data.clicked_price,
        duration_ms: data.duration_ms,
        error_type: data.error_type,
        message: data.message
    });
}

const OFFER_SELECTOR: string = ".itinerary";
const OFFER_BUTTON_SELECTOR: string = 'input[type="submit"]';
const INTERACTION_EVENTS: string[] = ["click", "touchstart"];

let hasInteracted: boolean = false;

function markInteracted(event: Event): void {
    if (hasInteracted) {
        return;
    }

    const target = event.target as Element;
    if (!target || !target.matches(OFFER_BUTTON_SELECTOR) || !target.closest(OFFER_SELECTOR)) {
        return;
    }

    hasInteracted = true;
    INTERACTION_EVENTS.forEach((eventName: string): void => {
        document.removeEventListener(eventName, markInteracted);
    });

    logFunnelEvent("interaction");
}

/**
 * Wires up interaction detection for the current page: a click or tap on
 * an offer's own submit button (input[type="submit"], the "select this
 * fare" button - class names differ per itinerary layout variant: "fly",
 * "continue-btn", "hotel", etc., but type="submit" is consistent) inside
 * an offer (`form.itinerary`, distinct from the "fake-itinerary" loading
 * placeholders) reports a single "interaction" event. Clicking anywhere
 * else - elsewhere on the offer card, filters, page chrome - does not
 * count; per business definition, interaction means clicking an offer's
 * button specifically, not just the offer card.
 *
 * bounce_no_interaction is intentionally NOT computed here client-side -
 * there is no reliable way to catch "the visit ended" in every browser
 * (visibilitychange/pagehide support and timing vary, sendBeacon has its
 * own limits). Instead: bounce rate = 100% - (visits with "interaction"
 * among visits with "search_results_rendered"), computed in Kibana. The
 * absence of an interaction event is the signal; nothing needs to fire
 * at the moment the visitor leaves.
 */
export function initInteractionTracking(): void {
    INTERACTION_EVENTS.forEach((eventName: string): void => {
        document.addEventListener(eventName, markInteracted, { passive: true });
    });
}
